import * as Yup from 'yup'
import oneOfMapper from 'utils/validators/oneOfMapper'
import tinValidator from 'utils/validators/tinValidator'
import phoneValidator from 'utils/validators/phoneValidator'
import emailValidator from 'utils/validators/emailValidator'
import { IS_APP_PRODUCT_GETUNIQ } from 'utils/constants/product'

const TAX_SYSTEM_INDIVIDUAL = [
  'simple',
  'basic',
  'automated_simple',
  'patent',
  'professional_income',
]

const TAX_SYSTEM_JURIDICAL = ['simple', 'basic', 'automated_simple']

const schema = ({ legalTypes = [], formValues = {} }) => {
  const { method /*, details*/ } = formValues

  const commonFields = Yup.object().shape({
    apply_agreement: Yup.boolean()
      .oneOf([true], 'common::validation_messages.required')
      .required('common::validation_messages.required'),
    method: Yup.string().oneOf(legalTypes, ({ values }) =>
      oneOfMapper(
        values,
        'common::validation_messages.oneOf',
        `payment_profiles::new.fields.method.options.`,
      ),
    ),
    details: Yup.object().shape({
      country_code: Yup.string().required('common::validation_messages.required'),
    }),
  })

  let detailsSchema

  if (method === 'personal') {
    if (/*details?.country_code === 'ru'*/ IS_APP_PRODUCT_GETUNIQ) {
      detailsSchema = Yup.object().shape({
        details: Yup.object().shape({
          country_code: Yup.string().required('common::validation_messages.required'),
          lastname: Yup.string().required('common::validation_messages.required'),
          firstname: Yup.string().required('common::validation_messages.required'),
          middle_name: Yup.string().required('common::validation_messages.required'),
          tax_number: Yup.string()
            .test(
              'tinValidator',
              'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.validation_messages.incorrect_tin_number',
              tinValidator,
            )
            .required('common::validation_messages.required'),
          phone: Yup.string()
            .test(
              'phoneValidator',
              'common::validation_messages.incorrect_phone_format',
              phoneValidator,
            )
            .required('common::validation_messages.required'),
        }),
      })
    } else {
      detailsSchema = Yup.object().shape({
        details: Yup.object().shape({
          country_code: Yup.string().required('common::validation_messages.required'),
          first_name: Yup.string().required('common::validation_messages.required'),
          last_name: Yup.string().required('common::validation_messages.required'),
          phone: Yup.string()
            .test(
              'phoneValidator',
              'common::validation_messages.incorrect_phone_format',
              phoneValidator,
            )
            .required('common::validation_messages.required'),
          tax_number: Yup.string().required('common::validation_messages.required'),
        }),
      })
    }
  } else {
    if (/*details?.country_code === 'ru'*/ IS_APP_PRODUCT_GETUNIQ) {
      detailsSchema = Yup.object().shape({
        details: Yup.object().shape({
          country_code: Yup.string().required('common::validation_messages.required'),
          organisation_name: Yup.string().required('common::validation_messages.required'),
          legal_personality: Yup.string()
            .oneOf(['juridical', 'juridical_ano', 'individual'])
            .required('common::validation_messages.required'),
          tax_system: Yup.string()
            .when('legal_personality', {
              is: (val) => val === 'individual',
              then: (schema) =>
                schema.oneOf(TAX_SYSTEM_INDIVIDUAL, ({ values }) =>
                  oneOfMapper(
                    values,
                    'common::validation_messages.oneOf',
                    'payment_profiles::new.fields.details.tax_system.options.',
                  ),
                ),
              otherwise: (schema) =>
                schema.oneOf(TAX_SYSTEM_JURIDICAL, ({ values }) =>
                  oneOfMapper(
                    values,
                    'common::validation_messages.oneOf',
                    'payment_profiles::new.fields.details.tax_system.options.',
                  ),
                ),
            })
            .required('common::validation_messages.required'),
          tax_number: Yup.string()
            .test(
              'tinValidator',
              'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.validation_messages.incorrect_tin_number',
              tinValidator,
            )
            .required('common::validation_messages.required'),
          kpp: Yup.string().when('legal_personality', {
            is: (legal_personality) => !!legal_personality && legal_personality !== 'individual',
            then: () => Yup.string().required('common::validation_messages.required'),
            otherwise: (schema) => schema.notRequired(),
          }),
          ogrn: Yup.string().when('legal_personality', {
            is: (legal_personality) => !!legal_personality && legal_personality !== 'individual',
            then: () => Yup.string().required('common::validation_messages.required'),
            otherwise: (schema) => schema.notRequired(),
          }),
          ogrnip: Yup.string().when('legal_personality', {
            is: (legal_personality) => !!legal_personality && legal_personality === 'individual',
            then: () => Yup.string().required('common::validation_messages.required'),
            otherwise: (schema) => schema.notRequired(),
          }),
          bic: Yup.string().required('common::validation_messages.required'),
          bank_name: Yup.string().required('common::validation_messages.required'),
          checking_account: Yup.string().required('common::validation_messages.required'),
          correspondent_account: Yup.string().required('common::validation_messages.required'),
          general_director: Yup.string().when('legal_personality', {
            is: (legal_personality) => !!legal_personality && legal_personality !== 'individual',
            then: () => Yup.string().required('common::validation_messages.required'),
            otherwise: (schema) => schema.notRequired(),
          }),
          juridical_address: Yup.string().required('common::validation_messages.required'),
          is_same_address: Yup.boolean(),
          actual_address: Yup.string().when('is_same_address', {
            is: (val) => !!val,
            then: (schema) => schema.strip(),
            otherwise: (schema) => schema.required('common::validation_messages.required'),
          }),
          contact_person: Yup.string(),
          phone: Yup.string().test(
            'phoneValidator',
            'common::validation_messages.incorrect_phone_format',
            phoneValidator,
          ),
          email: Yup.string(),
          currency: Yup.string(),
        }),
      })
    } else {
      detailsSchema = Yup.object().shape({
        details: Yup.object().shape({
          organisation_name: Yup.string().required('common::validation_messages.required'),
          country_code: Yup.string().required('common::validation_messages.required'),
          company_registration_number: Yup.string().required(
            'common::validation_messages.required',
          ),
          registration_address: Yup.string().required('common::validation_messages.required'),
          tax_number: Yup.string(),
          beneficiary_name: Yup.string().required('common::validation_messages.required'),
          phone: Yup.string().test(
            'phoneValidator',
            'common::validation_messages.incorrect_phone_format',
            phoneValidator,
          ),
          contact_email: Yup.string().test(
            'emailValidator',
            'common::validation_messages.incorrect_email_format',
            emailValidator,
          ),
          bank_address: Yup.string().required('common::validation_messages.required'),
          bank_account_number: Yup.string().required('common::validation_messages.required'),
          iban: Yup.string().required('common::validation_messages.required'),
          swift: Yup.string().required('common::validation_messages.required'),
        }),
      })
    }
  }

  return commonFields.concat(detailsSchema)
}

export default schema
